const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface AIChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  sources?: {
    agency: string;
    url: string;
    phone?: string;
  }[];
  quickActions?: {
    label: string;
    url: string;
  }[];
}

export async function askAIRecoveryAssistant(userQuery: string): Promise<AIChatMessage> {
  await delay(900); // AI generation latency simulation

  const q = userQuery.toLowerCase();
  
  let responseText = '';
  const sources: AIChatMessage['sources'] = [];
  const quickActions: AIChatMessage['quickActions'] = [];

  if (q.includes('licence') || q.includes('license') || q.includes('driver')) {
    responseText = `**Immediate Action for Compromised Australian Driver Licences:**\n\n1. **Replace Your Physical Card Number:** Under Australia's Document Verification Service (DVS), both the Licence Number and the Card Number printed on your card are matched. Getting a replacement card generates a new Card Number, which invalidates the old stolen card for online identity verification.\n2. **Contact Your State Road Authority:** Log in to Service NSW, VicRoads, QLD TMR, or your state registry to order a replacement card immediately.\n3. **Place a Free Credit Ban:** Contact Equifax on 138 332 to put a 21-day hold on your credit file so scammers cannot take loans in your name.`;
    sources.push({
      agency: 'IDCARE (Australia & NZ National Identity Support)',
      phone: '1800 595 160',
      url: 'https://www.idcare.org'
    });
    sources.push({
      agency: 'Document Verification Service (DVS)',
      url: 'https://www.idmatch.gov.au'
    });
    quickActions.push({ label: 'View Australian Licence Guide', url: '/identity-documents' });
    quickActions.push({ label: 'Start 8-Step Recovery Plan', url: '/identity-theft' });
  } else if (q.includes('medicare')) {
    responseText = `**Protecting a Compromised Medicare Card:**\n\n1. **Sign in to myGov:** Open your linked Medicare account and select 'Replace my card'. Select that your card was lost/stolen.\n2. **Receive New 10-Digit Number:** Services Australia will issue a card with a brand new 10-digit Medicare number and Individual Reference Number (IRN).\n3. **Call Services Australia Fraud Team:** Contact the Scams and Identity Theft Helpdesk on 1800 941 126 to flag your record.\n4. **Audit myGov Claims:** Check your recent Medicare claim history to verify no fraudulent claims were lodged.`;
    sources.push({
      agency: 'Services Australia Scams & ID Helpdesk',
      phone: '1800 941 126',
      url: 'https://www.servicesaustralia.gov.au'
    });
    quickActions.push({ label: 'Medicare Protection Guide', url: '/identity-documents' });
  } else if (q.includes('tax') || q.includes('tfn') || q.includes('ato')) {
    responseText = `**Protecting Your Tax File Number (TFN):**\n\n1. **Call ATO Client Identity Support:** Immediately call **1800 467 033** (Mon–Fri 8am–6pm AEST).\n2. **Place Security Lock:** The ATO will apply an enhanced security monitoring lock on your tax records, requiring additional authentication before tax returns or superannuation withdrawals can be processed.\n3. **Inspect myGov ATO Account:** Confirm that your linked bank account for tax refunds has not been modified by an unauthorized party.`;
    sources.push({
      agency: 'ATO Client Identity Support Centre',
      phone: '1800 467 033',
      url: 'https://www.ato.gov.au'
    });
    quickActions.push({ label: 'TFN Protection Guide', url: '/identity-documents' });
  } else if (q.includes('credit') || q.includes('loan') || q.includes('equifax')) {
    responseText = `**How to Place an Immediate Australian Credit Ban:**\n\n1. **Contact Equifax (138 332):** Submit an online credit ban request at equifax.com.au. The initial ban is active for 21 days free of charge.\n2. **Share Across Bureaus:** Tick the consent box asking Equifax to notify Experian (1300 783 684) and illion (1300 734 806) so all three Australian credit bureaus are locked simultaneously.\n3. **Extend to 12 Months:** If you have a police reference number or IDCARE case number, the ban can be extended up to 12 months for continued protection.`;
    sources.push({
      agency: 'Equifax Australia',
      phone: '138 332',
      url: 'https://www.equifax.com.au'
    });
    sources.push({
      agency: 'Australian Financial Complaints Authority (AFCA)',
      phone: '1800 931 678',
      url: 'https://www.afca.org.au'
    });
    quickActions.push({ label: 'Credit Ban Guide', url: '/resources/how-to-place-a-credit-ban-australia' });
  } else {
    responseText = `**Official Australian Identity Recovery Guidance:**\n\nDon't panic. Identity recovery is a methodical, step-by-step process:\n\n1. **Lock Down Communications:** Secure your email and banking apps first, and enable 2FA.\n2. **Freeze Financial Access:** Contact your bank fraud hotline and place a 21-day Credit Ban with Equifax.\n3. **Invalidate Stolen ID Cards:** Order replacement cards for your Driver Licence and Medicare to issue new Card Numbers on the Document Verification Service.\n4. **Get Free Case Support:** Contact **IDCARE on 1800 595 160**—they are Australia's official national non-profit identity support service.`;
    sources.push({
      agency: 'IDCARE National Support',
      phone: '1800 595 160',
      url: 'https://www.idcare.org'
    });
    sources.push({
      agency: 'ReportCyber (Australian Cyber Security Centre)',
      url: 'https://www.cyber.gov.au'
    });
    quickActions.push({ label: 'Launch 8-Step Recovery Wizard', url: '/identity-theft' });
  }

  return {
    id: 'msg-' + Date.now(),
    sender: 'assistant',
    text: responseText,
    timestamp: new Date().toISOString(),
    sources,
    quickActions
  };
}
